package presentation.screens.changePassword

import androidx.lifecycle.SavedStateHandle
import androidx.navigation.toRoute
import com.kinoshazam.Navigator
import com.kinoshazam.model.Route
import data.LoginRepository
import org.koin.core.component.KoinComponent
import presentation.screens.changePassword.ChangePasswordMVIAction.*
import utils.BaseViewModel
import utils.launch

class ChangePasswordViewModel(
    private val navigator: Navigator,
    private val repository: LoginRepository,
    savedStateHandle: SavedStateHandle,
) : BaseViewModel<ChangePasswordState, ChangePasswordMVIAction>(), KoinComponent {

    private val token = savedStateHandle.toRoute<Route.Dialog.ChangePassword>().token

    override fun initState(): ChangePasswordState = ChangePasswordState(token = token)

    override fun onAction(action: ChangePasswordMVIAction) {
        when (action) {
            is ChangeClicked -> {
                if (stateValue.isLoading) return
                if (!isValid()) return
                launch(
                    onError = {
                        updateState { copy(isLoading = false, isError = true) }
                    }
                ) {
                    updateState { copy(isLoading = true, isError = false) }
                    when {
                        stateValue.token != null -> {
                            repository.resetPassword(
                                token = requireNotNull(stateValue.token),
                                password = stateValue.newPassword
                            )
                            updateState { copy(isLoading = false) }
                            // After reset via email OTP — back to login.
                            navigator.navigateTo(Route.Dialog.Login, isClearStack = true)
                        }

                        else -> {
                            repository.changePassword(
                                currentPassword = stateValue.currentPassword,
                                newPassword = stateValue.newPassword,
                            )
                            updateState { copy(isLoading = false) }
                            navigator.back()
                        }
                    }
                }
            }

            is ConfirmPasswordChanged -> updateState {
                copy(
                    confirmPassword = action.password,
                    isConfirmPasswordError = false
                )
            }

            is CurrentPasswordChanged -> updateState {
                copy(
                    currentPassword = action.password,
                    isCurrentPasswordError = false
                )
            }

            is NewPasswordChanged -> updateState {
                copy(
                    newPassword = action.password,
                    isNewPasswordError = false
                )
            }

            is BackClicked -> navigator.back()
            is ErrorDismissed -> updateState { copy(isError = false) }
        }
    }

    private fun isValid(): Boolean {
        val current = stateValue.currentPassword
        val new = stateValue.newPassword
        val confirm = stateValue.confirmPassword

        val isCurrentValid = current.isNotEmpty() || stateValue.token != null

        val isPasswordValid = new.length >= 8 &&
                new.any { it.isDigit() } &&
                new.any { it.isUpperCase() } &&
                new.any { it.isLowerCase() }

        val isPasswordSame = new == confirm

        return if (!isCurrentValid || !isPasswordValid || !isPasswordSame) {
            updateState {
                copy(
                    isNewPasswordError = !isPasswordValid,
                    isCurrentPasswordError = !isCurrentValid,
                    isConfirmPasswordError = !isPasswordSame,
                )
            }
            false
        } else {
            true
        }
    }
}
